import React from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { TRIGGER_TYPES, ANCHOR_TYPES } from '../../utils/helpers';

/**
 * Shared installment editor — used by Create (TermFormModal) and Edit SO flows.
 */
export function InstallmentEditor({
  installments,
  setInstallments,
  currency = 'INR',
  showPresets = true,
  maxHeight = '50vh',
  readOnly = false,
}) {
  const totalPct = installments
    .filter(i => i.amount_type === 'PERCENTAGE')
    .reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
  const allFixed = installments.every(i => i.amount_type === 'FIXED_AMOUNT');
  const pctValid = allFixed || Math.abs(totalPct - 100) <= 0.01;

  const addInstallment = (afterIdx = null) => {
    const newInst = {
      sequence_no: 0,
      label: 'New Installment',
      amount_type: 'PERCENTAGE',
      amount_value: 0,
      trigger_type: 'DELIVERY_ACCEPTANCE',
      anchor_date_type: 'DELIVERY_DATE',
      due_offset_days: 0,
      scheduled_invoice_date: '',
      grace_period_days: 0,
      requires_buyer_approval: false,
    };
    setInstallments(prev => {
      let next;
      if (afterIdx === null) {
        next = [...prev, newInst];
      } else {
        next = [...prev.slice(0, afterIdx + 1), newInst, ...prev.slice(afterIdx + 1)];
      }
      return next.map((inst, i) => ({ ...inst, sequence_no: i + 1 }));
    });
  };

  const removeInstallment = (idx) =>
    setInstallments(prev =>
      prev.filter((_, i) => i !== idx).map((inst, i) => ({ ...inst, sequence_no: i + 1 }))
    );

  const updateInstallment = (idx, key, val) =>
    setInstallments(prev =>
      prev.map((inst, i) => (i === idx ? { ...inst, [key]: val } : inst))
    );

  const applyPreset = (type) => {
    const presets = {
      full_delivery: [{ seq: 1, label: 'Full Payment on Delivery', pct: 100, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 0 }],
      advance_50_50: [
        { seq: 1, label: 'Advance (50%)', pct: 50, trigger: 'PO_CONFIRMATION', anchor: 'PO_CONFIRM_DATE', offset: 0 },
        { seq: 2, label: 'Balance on Delivery (50%)', pct: 50, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 0 },
      ],
      advance_30_70: [
        { seq: 1, label: 'Advance (30%)', pct: 30, trigger: 'PO_CONFIRMATION', anchor: 'PO_CONFIRM_DATE', offset: 0 },
        { seq: 2, label: 'Balance on Delivery (70%)', pct: 70, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 0 },
      ],
      three_tranche: [
        { seq: 1, label: 'Advance (20%)', pct: 20, trigger: 'PO_CONFIRMATION', anchor: 'PO_CONFIRM_DATE', offset: 0 },
        { seq: 2, label: 'On Delivery (75%)', pct: 75, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 0 },
        { seq: 3, label: 'Retention (5%)', pct: 5, trigger: 'TIME_PERIOD', anchor: 'DELIVERY_DATE', offset: 90 },
      ],
      net30: [{ seq: 1, label: 'Full Payment Net 30', pct: 100, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 30 }],
      net60: [{ seq: 1, label: 'Full Payment Net 60', pct: 100, trigger: 'DELIVERY_ACCEPTANCE', anchor: 'DELIVERY_DATE', offset: 60 }],
    };
    const p = presets[type];
    if (p) {
      setInstallments(p.map(x => ({
        sequence_no: x.seq,
        label: x.label,
        amount_type: 'PERCENTAGE',
        amount_value: x.pct,
        trigger_type: x.trigger,
        anchor_date_type: x.anchor,
        due_offset_days: x.offset,
        scheduled_invoice_date: '',
        grace_period_days: 0,
        requires_buyer_approval: false,
      })));
    }
  };

  return (
    <div className="space-y-3">
      {showPresets && !readOnly && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Quick Presets</p>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'full_delivery', label: '100% on Delivery' },
              { key: 'advance_50_50', label: '50/50 Advance+Delivery' },
              { key: 'advance_30_70', label: '30/70 Advance+Delivery' },
              { key: 'three_tranche', label: '20/75/5 with Retention' },
              { key: 'net30', label: 'Net 30' },
              { key: 'net60', label: 'Net 60' },
            ].map(p => (
              <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-primary-100 hover:text-primary-700 text-gray-600 text-xs rounded-lg transition-colors">
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!allFixed && (
        <div className={`px-4 py-2.5 rounded-lg text-sm font-medium ${pctValid ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
          Installments total: {totalPct.toFixed(2)}% {pctValid ? '✅ Sums to 100%' : '⚠️ Must sum to 100%'}
        </div>
      )}

      <div className="space-y-3 overflow-y-auto" style={{ maxHeight, paddingRight: 4 }}>
        {installments.map((inst, idx) => (
          <div key={idx} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-700">
                Installment {inst.sequence_no}
                {inst.trigger_type === 'TIME_PERIOD' && inst.scheduled_invoice_date && (
                  <span className="ml-2 text-xs font-normal text-amber-600">
                    📅 Auto-invoice on {inst.scheduled_invoice_date}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <>
                    <button type="button" onClick={() => addInstallment(idx)}
                      className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-primary-100 text-gray-500 hover:text-primary-700">
                      + Insert after
                    </button>
                    {installments.length > 1 && (
                      <button type="button" onClick={() => removeInstallment(idx)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="label text-xs">Label</label>
                <input className="input text-sm" value={inst.label} readOnly={readOnly}
                  onChange={e => updateInstallment(idx, 'label', e.target.value)} />
              </div>
              <div>
                <label className="label text-xs">Amount Type</label>
                <select className="select text-sm" value={inst.amount_type} disabled={readOnly}
                  onChange={e => updateInstallment(idx, 'amount_type', e.target.value)}>
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED_AMOUNT">Fixed Amount</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">{inst.amount_type === 'PERCENTAGE' ? 'Percentage (%)' : `Amount (${currency})`}</label>
                <input type="number" step="0.01" className="input text-sm" value={inst.amount_value} readOnly={readOnly}
                  onChange={e => updateInstallment(idx, 'amount_value', e.target.value)} />
              </div>
              <div>
                <label className="label text-xs">Trigger</label>
                <select className="select text-sm" value={inst.trigger_type} disabled={readOnly}
                  onChange={e => updateInstallment(idx, 'trigger_type', e.target.value)}>
                  {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {inst.trigger_type === 'TIME_PERIOD' ? (
                <div>
                  <label className="label text-xs">Scheduled Invoice Date <span className="text-amber-500">*</span></label>
                  <input type="date" className="input text-sm" value={inst.scheduled_invoice_date || ''} readOnly={readOnly}
                    onChange={e => updateInstallment(idx, 'scheduled_invoice_date', e.target.value)} />
                </div>
              ) : (
                <>
                  <div>
                    <label className="label text-xs">Anchor Date</label>
                    <select className="select text-sm" value={inst.anchor_date_type} disabled={readOnly}
                      onChange={e => updateInstallment(idx, 'anchor_date_type', e.target.value)}>
                      {ANCHOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label text-xs">Due Offset (days)</label>
                    <input type="number" min="0" className="input text-sm" value={inst.due_offset_days || 0} readOnly={readOnly}
                      onChange={e => updateInstallment(idx, 'due_offset_days', parseInt(e.target.value, 10) || 0)} />
                  </div>
                </>
              )}
              <div>
                <label className="label text-xs">Grace Period (days)</label>
                <input type="number" min="0" className="input text-sm" value={inst.grace_period_days || 0} readOnly={readOnly}
                  onChange={e => updateInstallment(idx, 'grace_period_days', parseInt(e.target.value, 10) || 0)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <button type="button" onClick={() => addInstallment()} className="btn-secondary w-full justify-center text-sm">
          <PlusCircle size={15} /> Add Installment at End
        </button>
      )}
    </div>
  );
}

export function validateInstallmentPct(installments) {
  const totalPct = installments
    .filter(i => i.amount_type === 'PERCENTAGE')
    .reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
  const allFixed = installments.every(i => i.amount_type === 'FIXED_AMOUNT');
  return allFixed || Math.abs(totalPct - 100) <= 0.01;
}

/** Fixed installments must not exceed poValue; mixed/pct validated separately. */
export function validateInstallmentsAgainstPoValue(installments, poValue) {
  if (!installments?.length) return { valid: true };
  const value = parseFloat(poValue);
  if (!Number.isFinite(value) || value <= 0) return { valid: true };

  const allPct = installments.every(i => i.amount_type === 'PERCENTAGE');
  if (allPct) {
    const totalPct = installments.reduce((s, i) => s + parseFloat(i.amount_value || 0), 0);
    if (Math.abs(totalPct - 100) > 0.01) {
      return { valid: false, error: `Installment percentages must sum to 100%. Current sum: ${totalPct}%` };
    }
    return { valid: true };
  }

  let fixedTotal = 0;
  let pctBase = 0;
  for (const inst of installments) {
    if (inst.amount_type === 'FIXED_AMOUNT') {
      fixedTotal += parseFloat(inst.amount_value || 0);
    } else {
      pctBase += parseFloat(inst.amount_value || 0);
    }
  }
  const total = fixedTotal + (pctBase / 100) * value;
  if (total > value + 0.01) {
    return {
      valid: false,
      error: `Total installment amounts (${total.toFixed(2)}) exceed SO value (${value.toFixed(2)})`,
    };
  }
  return { valid: true };
}
